import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  countryList,
  countryDetail,
  createCountry,
  updateCountry,
  deleteCountry,
  stateList,
  stateDetail,
  createState,
  updateState,
  deleteState,
  cityList,
  cityDetail,
  createCity,
  updateCity,
  deleteCity,
} from "./callbacks";
import { QData } from "../../../types/basic-type-list";
import { ICountry, IState, ICity, LocationFormValues } from "../../../types/api/location";
import { TResponseWithQData } from "../../../types/common";

export const LOCATION_KEYS = {
  allCountries: ["countries"] as const,
  countryLists: () => [...LOCATION_KEYS.allCountries, "list"] as const,
  countryList: (qData: QData) => [...LOCATION_KEYS.countryLists(), qData] as const,
  
  allStates: ["states"] as const,
  stateLists: () => [...LOCATION_KEYS.allStates, "list"] as const,
  stateList: (qData: QData) => [...LOCATION_KEYS.stateLists(), qData] as const,

  allCities: ["cities"] as const,
  cityLists: () => [...LOCATION_KEYS.allCities, "list"] as const,
  cityList: (qData: QData) => [...LOCATION_KEYS.cityLists(), qData] as const,
};

// --- COUNTRY HOOKS ---
export function useCountryList(
  qData: QData & { search?: string; status?: string },
  options?: any
) {
  // `liveQData` (not the raw `qData` prop) drives the actual query below, so
  // refetchWithQuery() - called by ApiSelect on every search keystroke - can
  // change what's fetched/displayed. The old version only prefetched a
  // *different* cache key and never touched what this hook returned, so
  // admin country/state/city search boxes looked like they searched but the
  // dropdown never actually updated past whatever the initial fetch had
  // (Oliver's testing doc, round 7 - only first 100 shown, breaks past
  // Hungary alphabetically).
  const [liveQData, setLiveQData] = useState(qData);
  const qDataKey = JSON.stringify(qData);
  useEffect(() => {
    setLiveQData(qData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDataKey]);

  const query = useQuery<TResponseWithQData<ICountry[]>>({
    queryKey: LOCATION_KEYS.countryList(liveQData),
    queryFn: async () => {
      const { data } = await countryList(liveQData);
      return data;
    },
    ...options,
  });

  const refetchWithQuery = (newQData: typeof qData) => {
    setLiveQData(newQData);
  };

  return { ...query, refetchWithQuery };
}

export function useCountryCreate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: any;
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LocationFormValues) => createCountry(payload),
    onSuccess: (res) => {
      if (qData) {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.countryList(qData) });
      } else {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.countryLists() });
      }
      onSuccess?.(res);
    },
    onError: (error: any) => {
      onError?.(error);
    },
  });
}

export function useCountryUpdate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: any;
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<LocationFormValues> }) =>
      updateCountry({ id, payload }),
    onSuccess: (res) => {
      if (qData) {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.countryList(qData) });
      } else {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.countryLists() });
      }
      onSuccess?.(res);
    },
    onError: (error: any) => {
      onError?.(error);
    },
  });
}

export function useCountryDelete({
  qData,
  onSuccess,
  onError,
}: {
  qData?: any;
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCountry(id),
    onSuccess: (res) => {
      if (qData) {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.countryList(qData) });
      } else {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.countryLists() });
      }
      onSuccess?.(res);
    },
    onError: (error: any) => {
      onError?.(error);
    },
  });
}

// --- STATE HOOKS ---
export function useStateList(
  qData: QData & { search?: string; status?: string; countryId?: string },
  options?: any
) {
  // See useCountryList above for why liveQData (not the raw qData prop)
  // drives the query.
  const [liveQData, setLiveQData] = useState(qData);
  const qDataKey = JSON.stringify(qData);
  useEffect(() => {
    setLiveQData(qData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDataKey]);

  const query = useQuery<TResponseWithQData<IState[]>>({
    queryKey: LOCATION_KEYS.stateList(liveQData),
    queryFn: async () => {
      const { data } = await stateList(liveQData);
      return data;
    },
    ...options,
  });

  const refetchWithQuery = (newQData: typeof qData) => {
    setLiveQData(newQData);
  };

  return { ...query, refetchWithQuery };
}

export function useStateCreate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: any;
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LocationFormValues) => createState(payload),
    onSuccess: (res) => {
      if (qData) {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.stateList(qData) });
      } else {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.stateLists() });
      }
      onSuccess?.(res);
    },
    onError: (error: any) => {
      onError?.(error);
    },
  });
}

export function useStateUpdate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: any;
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<LocationFormValues> }) =>
      updateState({ id, payload }),
    onSuccess: (res) => {
      if (qData) {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.stateList(qData) });
      } else {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.stateLists() });
      }
      onSuccess?.(res);
    },
    onError: (error: any) => {
      onError?.(error);
    },
  });
}

export function useStateDelete({
  qData,
  onSuccess,
  onError,
}: {
  qData?: any;
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteState(id),
    onSuccess: (res) => {
      if (qData) {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.stateList(qData) });
      } else {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.stateLists() });
      }
      onSuccess?.(res);
    },
    onError: (error: any) => {
      onError?.(error);
    },
  });
}

// --- CITY HOOKS ---
export function useCityList(
  qData: QData & { search?: string; status?: string; countryId?: string; stateId?: string },
  options?: any
) {
  // See useCountryList above for why liveQData (not the raw qData prop)
  // drives the query.
  const [liveQData, setLiveQData] = useState(qData);
  const qDataKey = JSON.stringify(qData);
  useEffect(() => {
    setLiveQData(qData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDataKey]);

  const query = useQuery<TResponseWithQData<ICity[]>>({
    queryKey: LOCATION_KEYS.cityList(liveQData),
    queryFn: async () => {
      const { data } = await cityList(liveQData);
      return data;
    },
    ...options,
  });

  const refetchWithQuery = (newQData: typeof qData) => {
    setLiveQData(newQData);
  };

  return { ...query, refetchWithQuery };
}

export function useCityCreate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: any;
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: LocationFormValues) => createCity(payload),
    onSuccess: (res) => {
      if (qData) {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.cityList(qData) });
      } else {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.cityLists() });
      }
      onSuccess?.(res);
    },
    onError: (error: any) => {
      onError?.(error);
    },
  });
}

export function useCityUpdate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: any;
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<LocationFormValues> }) =>
      updateCity({ id, payload }),
    onSuccess: (res) => {
      if (qData) {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.cityList(qData) });
      } else {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.cityLists() });
      }
      onSuccess?.(res);
    },
    onError: (error: any) => {
      onError?.(error);
    },
  });
}

export function useCityDelete({
  qData,
  onSuccess,
  onError,
}: {
  qData?: any;
  onSuccess?: (res?: any) => void;
  onError?: (err?: any) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCity(id),
    onSuccess: (res) => {
      if (qData) {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.cityList(qData) });
      } else {
        queryClient.invalidateQueries({ queryKey: LOCATION_KEYS.cityLists() });
      }
      onSuccess?.(res);
    },
    onError: (error: any) => {
      onError?.(error);
    },
  });
}
